import { FC, Fragment, memo, RefObject, useMemo, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import { Controller, SubmitHandler } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Masonry from '@mui/lab/Masonry'
import OutlinedInput from '@mui/material/OutlinedInput'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import {
  PlanningApplicationStatus,
  PlanningApplicationType,
  allPlanningApplicationTypes,
} from '@leadgen/models/client'
import useCallbackDebounced from '../../../../hooks/shared/useCallbackDebounced'
import { usePlanningRecordForm, FormData } from '../../../../forms/planningRecordForm'
import config from '../../../../config'
import { PdfPreviewPanel } from '../../../atoms/PdfPreviewPanel'
import SplitPreviewLayout, { SplitPreviewLayoutHandles } from '../../../layouts/SplitPreviewLayout'
import { stringFormatter } from '../../../../formatters/stringFormatter'
import Typography from '@mui/material/Typography'
import AutoCompleteInput from '../../../atoms/AutoCompleteInput'
import { RadioButton } from '../../../atoms/RadioButton'
import {
  CheckboxButtonContainer,
  CheckboxButtonContainerOption,
} from '../../../atoms/CheckboxButton'
import { ArrayToggleController } from '../../../form/ArrayToggleController'
import { usePlanningRecord } from '../../../../hooks/planningRecords/usePlanningRecord'
import CircularProgress from '../../../atoms/CircularProgress'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  planningCategoryGroups,
  planningCategoryMap,
  planningClassificationGroups,
  planningClassificationMap,
} from '../../../../constants/planningTaxonomy'

const planningApplicationTypeMap: Record<
  PlanningApplicationType,
  { title: string; description: string }
> = {
  listed: {
    title: 'Listed',
    description: 'Listed building consent planning applications.',
  },
  unlisted: {
    title: 'Unlisted',
    description: 'Planning applications for works to unlisted properties.',
  },
}

const planningApplicationStatusTitleMap: Record<PlanningApplicationStatus, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
}

const getFileUrl = ({
  planningRecordId,
  fileName,
}: {
  planningRecordId: string
  fileName: string
}) => {
  const encodedUrlPart = encodeURIComponent(`planningRecordDocs/${planningRecordId}/${fileName}`)
  const url = `${config.bucketBaseUrl}${encodedUrlPart}?alt=media`
  return url
}

const ApplicationFileRow: FC<{
  selectedFile: string | undefined
  applicationFile: string
  setSelectedFile: (applicationFile: string) => void
  splitPreviewLayoutRef: RefObject<SplitPreviewLayoutHandles>
  // eslint-disable-next-line react/display-name
}> = memo(({ selectedFile, applicationFile, setSelectedFile, splitPreviewLayoutRef }) => {
  const applicationFilePressed = useCallbackDebounced(() => {
    setSelectedFile(applicationFile)
    splitPreviewLayoutRef.current?.setTabIndex(1)
  }, [applicationFile, setSelectedFile, splitPreviewLayoutRef])
  return (
    <Chip
      key={applicationFile}
      label={stringFormatter.formatFileName(applicationFile)}
      variant={selectedFile === applicationFile ? 'filled' : 'outlined'}
      onClick={applicationFilePressed}
    />
  )
})

const PlanningRecordReviewPanel: FC<{
  onClose: () => void
  planningRecordId: string
}> = ({ onClose, planningRecordId }) => {
  const sm = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const md = useMediaQuery(theme => theme.breakpoints.between('sm', 'md'))
  const lg = useMediaQuery(theme => theme.breakpoints.between('md', 'lg'))
  const xl = useMediaQuery(theme => theme.breakpoints.between('lg', 'xl'))
  const xxl = useMediaQuery(theme => theme.breakpoints.between('xl', 'widescreen'))
  const widescreen = useMediaQuery(theme => theme.breakpoints.up('widescreen'))
  const splitPreviewLayoutRef = useRef<SplitPreviewLayoutHandles>(null)
  const [selectedFile, setSelectedFile] = useState<string>()
  const { planningRecord, loading } = usePlanningRecord({
    planningRecordId,
    onLoadComplete: planningRecord => {
      if (planningRecord.applicationFile || planningRecord.decisionFile) {
        setSelectedFile((planningRecord.applicationFile || planningRecord.decisionFile) as string)
      }
    },
  })
  const selectedFileUrl = useMemo(() => {
    if (!selectedFile) {
      return
    }
    return getFileUrl({
      planningRecordId,
      fileName: selectedFile,
    })
  }, [planningRecordId, selectedFile])
  const alreadyActioned =
    planningRecord?.status === 'validated' || planningRecord?.status === 'discarded'
  const {
    control,
    formState,
    reset,
    handleSubmitApprove,
    handleSubmitDiscard,
    onSubmitApprove: onSubmitApproveBase,
    onSubmitDiscard: onSubmitDiscardBase,
    showApplicantFields,
    showApplicantAddressFields,
    showAgentFields,
    showAgentAddressFields,
    showPostcodeField,
    showAddressFields,
    recipientType,
    selectSuggestionApplicant,
    suggestionsApplicant,
    loadingAutocomleteApplicant,
    loadingAutocomleteApplicantSelection,
    selectSuggestionAgent,
    suggestionsAgent,
    loadingAutocomleteAgent,
    loadingAutocomleteAgentSelection,
    selectSuggestionAddress,
    suggestionsAddress,
    loadingAutocomleteAddress,
    loadingAutocomleteAddressSelection,
    showClassifications,
    showCategories,
  } = usePlanningRecordForm({ planningRecord })
  const [confirmApproveModalOpen, setConfrmApproveModalOpen] = useState(false)
  const [confirmDiscardModalOpen, setConfrmDiscardModalOpen] = useState(false)

  const handleCloseReviewEditModal = useCallbackDebounced(() => {
    reset()
    setConfrmApproveModalOpen(false)
    setConfrmDiscardModalOpen(false)
  }, [reset])
  const handleClickApprove = useCallbackDebounced(() => {
    setConfrmDiscardModalOpen(false)
    setConfrmApproveModalOpen(true)
  }, [])
  const handleClickDiscard = useCallbackDebounced(() => {
    setConfrmDiscardModalOpen(true)
    setConfrmApproveModalOpen(false)
  }, [])
  const handleCloseConfirmApproveModal = useCallbackDebounced(() => {
    setConfrmApproveModalOpen(false)
  }, [])
  const handleCloseConfirmDiscardModal = useCallbackDebounced(() => {
    setConfrmDiscardModalOpen(false)
  }, [])
  const onSubmitApprove: SubmitHandler<FormData> = useCallbackDebounced(
    async (formData: FormData) => {
      await onSubmitApproveBase(formData)
      handleCloseReviewEditModal()
      onClose()
    },
    [handleCloseReviewEditModal, onSubmitApproveBase, onClose],
  )
  const onSubmitDiscard: SubmitHandler<{ id: string }> = useCallbackDebounced(
    async formData => {
      await onSubmitDiscardBase(formData)
      handleCloseReviewEditModal()
      onClose()
    },
    [handleCloseReviewEditModal, onSubmitDiscardBase, onClose],
  )
  return (
    <>
      <SplitPreviewLayout
        ref={splitPreviewLayoutRef}
        id="company-planning-record-details"
        onClose={onClose}
        preview={
          <PdfPreviewPanel
            url={selectedFileUrl}
            renderAnnotationLayer={true}
            renderTextLayer={true}
          />
        }
        details={
          <>
            {planningRecord && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Chip
                    label={`Planning application status: ${
                      planningApplicationStatusTitleMap[planningRecord.planningApplicationStatus]
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Typography
                    variant="h6"
                    component="a"
                    href={planningRecord.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    sx={theme => ({
                      outline: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.text.primary,
                    })}
                  >
                    {alreadyActioned &&
                      `(${planningRecord.status === 'validated' ? 'approved' : 'discarded'}): `}
                    Application {planningRecord.id}
                    <OpenInNewIcon sx={{ marginBottom: 0.5, marginLeft: 0.5 }} />
                  </Typography>
                  <Typography variant={'body2'}>{planningRecord.scrapedDescriptionText}</Typography>
                  <Typography variant={'body2'}>{planningRecord.proposalSummary}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 2,
                  }}
                >
                  <Typography variant="h6" component="div">
                    Site address
                  </Typography>
                </Box>
                <Controller
                  name="postcode"
                  control={control}
                  render={({ field }) => {
                    if (!showPostcodeField) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Postcode *</Typography>
                        <AutoCompleteInput
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAddressSelection}
                          name={field.name}
                          selectSuggestion={selectSuggestionAddress}
                          suggestions={suggestionsAddress}
                          loading={loadingAutocomleteAddress}
                          loadingSelection={loadingAutocomleteAddressSelection}
                          error={
                            !loadingAutocomleteAddressSelection &&
                            formState.errors['postcode']?.type === 'format'
                          }
                        />
                        {!loadingAutocomleteAddressSelection &&
                          !!formState.errors['postcode']?.message && (
                            <Typography
                              variant="subtitle1"
                              color={
                                formState.errors['postcode']?.type === 'format'
                                  ? 'error'
                                  : undefined
                              }
                              sx={{
                                fontSize: 12,
                              }}
                            >
                              {formState.errors['postcode']?.message}
                            </Typography>
                          )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="address1"
                  control={control}
                  render={({ field }) => {
                    if (!showAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 1 *</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAddressSelection}
                          name={field.name}
                          error={formState.errors['address1']?.type === 'format'}
                        />
                        {!!formState.errors['address1']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['address1']?.type === 'format' ? 'error' : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['address1'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="address2"
                  control={control}
                  render={({ field }) => {
                    if (!showAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 2</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAddressSelection}
                          name={`${field.name}-search`}
                          error={formState.errors['address2']?.type === 'format'}
                        />
                        {!!formState.errors['address2']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['address2']?.type === 'format' ? 'error' : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['address2'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="town"
                  control={control}
                  render={({ field }) => {
                    if (!showAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Town</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAddressSelection}
                          error={formState.errors['town']?.type === 'format'}
                        />
                        {!!formState.errors['town']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['town']?.type === 'format' ? 'error' : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['town'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                {!showPostcodeField && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant={'body2'}>{planningRecord.address}</Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 2,
                  }}
                >
                  <Typography variant="h6" component="div">
                    Specification
                  </Typography>
                </Box>
                <Controller
                  name="applicationType"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Type of planning application</Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          {allPlanningApplicationTypes.map(applicationType => (
                            <RadioButton
                              key={applicationType}
                              disabled={alreadyActioned}
                              onBlur={field.onBlur}
                              onChange={field.onChange}
                              value={applicationType}
                              checked={field.value === applicationType}
                              label={planningApplicationTypeMap[applicationType].title}
                              tooltip={planningApplicationTypeMap[applicationType].description}
                            />
                          ))}
                        </Box>
                        {!!formState.errors['applicationType']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicationType']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicationType'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <ArrayToggleController
                  name="classifications"
                  control={control}
                  render={({ field }) => {
                    if (!showClassifications) {
                      return <Box component={'span'} />
                    }
                    const selectedPlanningClassificationsSet = new Set(field.value)
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Site or work type</Typography>
                        <Masonry
                          columns={
                            sm ? 1 : md ? 2 : lg ? 1 : xl ? 2 : xxl ? 3 : widescreen ? 4 : 100
                          }
                          spacing={1}
                        >
                          {planningClassificationGroups.map(group => (
                            <CheckboxButtonContainer key={group.id} label={group.title}>
                              {group.options.map(planningClassification => (
                                <CheckboxButtonContainerOption
                                  key={planningClassification}
                                  disabled={alreadyActioned}
                                  onChange={field.onChange}
                                  onBlur={field.onBlur}
                                  value={planningClassification}
                                  checked={selectedPlanningClassificationsSet.has(
                                    planningClassification,
                                  )}
                                  label={planningClassificationMap[planningClassification].title}
                                  tooltip={
                                    planningClassificationMap[planningClassification].description
                                  }
                                />
                              ))}
                            </CheckboxButtonContainer>
                          ))}
                        </Masonry>
                        {!!formState.errors['classifications']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['classifications']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['classifications'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <ArrayToggleController
                  name="categories"
                  control={control}
                  render={({ field }) => {
                    if (!showCategories) {
                      return <Box component={'span'} />
                    }
                    const selectedPlanningCategoriesSet = new Set(field.value)
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Associated works</Typography>
                        <Masonry
                          columns={
                            sm ? 1 : md ? 2 : lg ? 1 : xl ? 2 : xxl ? 3 : widescreen ? 4 : 100
                          }
                          spacing={1}
                        >
                          {planningCategoryGroups.map(group => (
                            <CheckboxButtonContainer
                              key={group.id}
                              label={group.title}
                              tooltip={group.description}
                            >
                              {group.options.map(planningCategory => (
                                <CheckboxButtonContainerOption
                                  key={planningCategory}
                                  disabled={alreadyActioned}
                                  onBlur={field.onBlur}
                                  onChange={field.onChange}
                                  value={planningCategory}
                                  checked={selectedPlanningCategoriesSet.has(planningCategory)}
                                  label={planningCategoryMap[planningCategory].title}
                                  tooltip={planningCategoryMap[planningCategory].description}
                                />
                              ))}
                            </CheckboxButtonContainer>
                          ))}
                        </Masonry>
                        {!!formState.errors['categories']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['categories']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['categories'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 2,
                  }}
                >
                  <Typography variant="h6" component="div">
                    Recipient
                  </Typography>
                </Box>
                <Controller
                  name="recipientType"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <ToggleButtonGroup
                        color="primary"
                        value={recipientType}
                        exclusive
                        onChange={field.onChange}
                        aria-label="Platform"
                        disabled={alreadyActioned}
                      >
                        <ToggleButton value="applicant">Applicant</ToggleButton>
                        <ToggleButton value="agent">Agent</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                />
                {showApplicantFields && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h6" component="div">
                      Applicant address
                    </Typography>
                  </Box>
                )}
                <Controller
                  name="applicantPostcode"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Postcode *</Typography>
                        <AutoCompleteInput
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteApplicantSelection}
                          name={field.name}
                          selectSuggestion={selectSuggestionApplicant}
                          suggestions={suggestionsApplicant}
                          loading={loadingAutocomleteApplicant}
                          loadingSelection={loadingAutocomleteApplicantSelection}
                          error={
                            !loadingAutocomleteApplicantSelection &&
                            formState.errors['applicantPostcode']?.type === 'format'
                          }
                        />
                        {!loadingAutocomleteApplicantSelection &&
                          !!formState.errors['applicantPostcode']?.message && (
                            <Typography
                              variant="subtitle1"
                              color={
                                formState.errors['applicantPostcode']?.type === 'format'
                                  ? 'error'
                                  : undefined
                              }
                              sx={{
                                fontSize: 12,
                              }}
                            >
                              {formState.errors['applicantPostcode'].message}
                            </Typography>
                          )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantAddress1"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 1 *</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteApplicantSelection}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantAddress1']?.type === 'format'}
                        />
                        {!!formState.errors['applicantAddress1']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantAddress1']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantAddress1'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantAddress2"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 2</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteApplicantSelection}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantAddress2']?.type === 'format'}
                        />
                        {!!formState.errors['applicantAddress2']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantAddress2']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantAddress2'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantTown"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Town</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteApplicantSelection}
                          error={formState.errors['applicantTown']?.type === 'format'}
                        />
                        {!!formState.errors['applicantTown']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantTown']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantTown'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                {showApplicantFields && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h6" component="div">
                      Applicant name
                    </Typography>
                  </Box>
                )}
                <Controller
                  name="applicantTitle"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Title</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantTitle']?.type === 'format'}
                        />
                        {!!formState.errors['applicantTitle']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantTitle']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantTitle'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantFirstName"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">First name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantFirstName']?.type === 'format'}
                        />
                        {!!formState.errors['applicantFirstName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantFirstName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantFirstName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantLastName"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Last name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantLastName']?.type === 'format'}
                        />
                        {!!formState.errors['applicantLastName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantLastName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantLastName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="applicantCompanyName"
                  control={control}
                  render={({ field }) => {
                    if (!showApplicantFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Company name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['applicantCompanyName']?.type === 'format'}
                        />
                        {!!formState.errors['applicantCompanyName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['applicantCompanyName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['applicantCompanyName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                {showAgentFields && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h6" component="div">
                      Agent address
                    </Typography>
                  </Box>
                )}
                <Controller
                  name="agentPostcode"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Postcode *</Typography>
                        <AutoCompleteInput
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAgentSelection}
                          name={field.name}
                          selectSuggestion={selectSuggestionAgent}
                          suggestions={suggestionsAgent}
                          loading={loadingAutocomleteAgent}
                          loadingSelection={loadingAutocomleteAgentSelection}
                          error={
                            !loadingAutocomleteAgentSelection &&
                            formState.errors['agentPostcode']?.type === 'format'
                          }
                        />
                        {!loadingAutocomleteAgentSelection &&
                          !!formState.errors['agentPostcode']?.message && (
                            <Typography
                              variant="subtitle1"
                              color={
                                formState.errors['agentPostcode']?.type === 'format'
                                  ? 'error'
                                  : undefined
                              }
                              sx={{
                                fontSize: 12,
                              }}
                            >
                              {formState.errors['agentPostcode'].message}
                            </Typography>
                          )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentAddress1"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 1 *</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAgentSelection}
                          name={`${field.name}-search`}
                          error={formState.errors['agentAddress1']?.type === 'format'}
                        />
                        {!!formState.errors['agentAddress1']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentAddress1']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentAddress1'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentAddress2"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Address line 2</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAgentSelection}
                          name={`${field.name}-search`}
                          error={formState.errors['agentAddress2']?.type === 'format'}
                        />
                        {!!formState.errors['agentAddress2']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentAddress2']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentAddress2'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentTown"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentAddressFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Town</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned || loadingAutocomleteAgentSelection}
                          error={formState.errors['agentTown']?.type === 'format'}
                        />
                        {!!formState.errors['agentTown']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentTown']?.type === 'format' ? 'error' : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentTown'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                {showAgentFields && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h6" component="div">
                      Agent name
                    </Typography>
                  </Box>
                )}
                <Controller
                  name="agentTitle"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Title</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['agentTitle']?.type === 'format'}
                        />
                        {!!formState.errors['agentTitle']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentTitle']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentTitle'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentFirstName"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">First name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['agentFirstName']?.type === 'format'}
                        />
                        {!!formState.errors['agentFirstName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentFirstName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentFirstName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentLastName"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Last name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['agentLastName']?.type === 'format'}
                        />
                        {!!formState.errors['agentLastName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentLastName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentLastName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Controller
                  name="agentCompanyName"
                  control={control}
                  render={({ field }) => {
                    if (!showAgentFields) {
                      return <Fragment />
                    }
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2">Company name</Typography>
                        <OutlinedInput
                          autoComplete="off"
                          autoCapitalize="off"
                          autoCorrect="off"
                          {...field}
                          value={field.value || ''}
                          readOnly={alreadyActioned}
                          name={`${field.name}-search`}
                          error={formState.errors['agentCompanyName']?.type === 'format'}
                        />
                        {!!formState.errors['agentCompanyName']?.message && (
                          <Typography
                            variant="subtitle1"
                            color={
                              formState.errors['agentCompanyName']?.type === 'format'
                                ? 'error'
                                : undefined
                            }
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {formState.errors['agentCompanyName'].message}
                          </Typography>
                        )}
                      </Box>
                    )
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 2,
                  }}
                >
                  <Typography variant="h6" component="div">
                    Files
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {planningRecord.applicationFile && (
                      <ApplicationFileRow
                        selectedFile={selectedFile}
                        splitPreviewLayoutRef={splitPreviewLayoutRef}
                        applicationFile={planningRecord.applicationFile}
                        setSelectedFile={setSelectedFile}
                      />
                    )}
                    {planningRecord.decisionFile && (
                      <ApplicationFileRow
                        selectedFile={selectedFile}
                        splitPreviewLayoutRef={splitPreviewLayoutRef}
                        applicationFile={planningRecord.decisionFile}
                        setSelectedFile={setSelectedFile}
                      />
                    )}
                  </Box>
                </Box>
                {!alreadyActioned && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      paddingTop: 2,
                    }}
                  >
                    <Button
                      id="submit-approve-button"
                      variant="contained"
                      loading={formState.isSubmitting}
                      disabled={
                        alreadyActioned ||
                        formState.isValidating ||
                        !formState.isValid ||
                        formState.isSubmitting ||
                        formState.isSubmitSuccessful
                      }
                      onClick={handleClickApprove}
                      sx={{ flex: 1 }}
                    >
                      approve
                    </Button>
                    <Button
                      id="submit-decline-button"
                      variant="contained"
                      color="error"
                      loading={formState.isSubmitting}
                      disabled={
                        alreadyActioned ||
                        formState.isValidating ||
                        formState.isSubmitting ||
                        formState.isSubmitSuccessful
                      }
                      onClick={handleClickDiscard}
                      sx={{ flex: 1 }}
                    >
                      discard
                    </Button>
                  </Box>
                )}
                {formState.errors.root?.message && (
                  <Typography
                    variant="subtitle1"
                    color="error"
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    {formState.errors.root?.message}
                  </Typography>
                )}
              </>
            )}
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress delay={200} />
              </Box>
            )}
          </>
        }
      />
      <Dialog open={confirmApproveModalOpen} onClose={handleCloseConfirmApproveModal}>
        <DialogTitle id="alert-dialog-title">Approve planning record?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to approve this planning record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmApproveModal}>No</Button>
          <Button onClick={handleSubmitApprove(onSubmitApprove)} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDiscardModalOpen} onClose={handleCloseConfirmDiscardModal}>
        <DialogTitle id="alert-dialog-title">Discard planning record?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to discard this planning record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDiscardModal}>No</Button>
          <Button onClick={handleSubmitDiscard(onSubmitDiscard)} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default memo(PlanningRecordReviewPanel)
